import type { PanZoomState } from './types';

export interface PanZoomCallbacks {
	onUpdate: (scale: number, panX: number, panY: number) => void;
}

export function createPanZoom(
	container: HTMLElement,
	svg: SVGSVGElement,
	mainGroup: SVGGElement,
	zoomInfo: HTMLElement,
	initialState: PanZoomState = { scale: 1, panX: 0, panY: 0 }
): {
	zoomIn: () => void;
	zoomOut: () => void;
	resetView: () => void;
	fitToScreen: (totalWidth: number, totalHeight: number) => void;
	cleanup: () => void;
	state: PanZoomState;
} {
	const state: PanZoomState = { ...initialState };
	let isDragging = false;
	let lastX = 0;
	let lastY = 0;

	// Touch state for pinch zoom
	let isTouching = false;
	let touchStartDistance = 0;
	let touchStartScale = 1;
	let lastTouchX = 0;
	let lastTouchY = 0;
	let isPinching = false;
	let touchCenterX = 0;
	let touchCenterY = 0;

	// Single RAF loop for all updates (pan + zoom)
	let rafId: number | null = null;
	let dirty = false;
	let isZoomDirty = false;

	// Pending pan deltas
	let pendingDX = 0;
	let pendingDY = 0;

	// Pending zoom state
	let pendingScale: number | null = null;
	let pendingClientX = 0;
	let pendingClientY = 0;

	// Promote main group to its own GPU layer permanently
	mainGroup.style.willChange = 'transform';

	function scheduleUpdate(): void {
		if (!rafId) {
			rafId = requestAnimationFrame(flushUpdate);
		}
	}

	function flushUpdate(): void {
		rafId = null;

		// Apply pending zoom first (recalculates pan too)
		if (pendingScale !== null) {
			const rect = container.getBoundingClientRect();
			const mouseX = pendingClientX - rect.left;
			const mouseY = pendingClientY - rect.top;

			const graphX = (mouseX - state.panX) / state.scale;
			const graphY = (mouseY - state.panY) / state.scale;

			state.scale = Math.max(0.25, Math.min(3, pendingScale));

			state.panX = mouseX - graphX * state.scale;
			state.panY = mouseY - graphY * state.scale;

			pendingScale = null;
			isZoomDirty = true;
		}

		// Apply pending pan deltas
		if (pendingDX !== 0 || pendingDY !== 0) {
			state.panX += pendingDX;
			state.panY += pendingDY;
			pendingDX = 0;
			pendingDY = 0;
		}

		// Single style write per frame
		mainGroup.style.transform = `translate(${state.panX}px, ${state.panY}px) scale(${state.scale})`;

		if (isZoomDirty) {
			const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
			const hintText = isTouchDevice 
				? `Zoom: ${Math.round(state.scale * 100)}% | Drag to pan • Pinch to zoom`
				: `Zoom: ${Math.round(state.scale * 100)}% | Drag to pan • Wheel to zoom`;
			zoomInfo.textContent = hintText;
			isZoomDirty = false;
		}
	}

	function applyImmediate(isZoom = false): void {
		mainGroup.style.transform = `translate(${state.panX}px, ${state.panY}px) scale(${state.scale})`;
		if (isZoom) {
			const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
			const hintText = isTouchDevice 
				? `Zoom: ${Math.round(state.scale * 100)}% | Drag to pan • Pinch to zoom`
				: `Zoom: ${Math.round(state.scale * 100)}% | Drag to pan • Wheel to zoom`;
			zoomInfo.textContent = hintText;
		}
	}

	function zoomIn(): void {
		const rect = container.getBoundingClientRect();
		pendingScale = state.scale * 1.25;
		pendingClientX = rect.left + rect.width / 2;
		pendingClientY = rect.top + rect.height / 2;
		scheduleUpdate();
	}

	function zoomOut(): void {
		const rect = container.getBoundingClientRect();
		pendingScale = state.scale / 1.25;
		pendingClientX = rect.left + rect.width / 2;
		pendingClientY = rect.top + rect.height / 2;
		scheduleUpdate();
	}

	function resetView(): void {
		state.scale = 1;
		state.panX = 0;
		state.panY = 0;
		applyImmediate(true);
	}

	function fitToScreen(totalWidth: number, totalHeight: number): void {
		const containerRect = container.getBoundingClientRect();
		// Account for timeline slider at bottom (approx 70px height)
		const TIMELINE_HEIGHT = 70;
		const availableHeight = containerRect.height - TIMELINE_HEIGHT;
		
		const fitScale = Math.min(
			(containerRect.width - 40) / totalWidth,
			(availableHeight - 40) / totalHeight,
			1
		);
		state.scale = Math.max(fitScale, 0.35);
		state.panX = (containerRect.width - totalWidth * state.scale) / 2;
		// Shift up slightly to account for timeline
		state.panY = (availableHeight - totalHeight * state.scale) / 2;
		applyImmediate(true);
	}

	// Mouse event handlers
	function handleMouseDown(e: MouseEvent): void {
		if ((e.target as HTMLElement).closest('.node')) return;
		isDragging = true;
		lastX = e.clientX;
		lastY = e.clientY;
		container.style.cursor = 'grabbing';
		svg.style.pointerEvents = 'none';
	}

	function handleMouseMove(e: MouseEvent): void {
		if (!isDragging) return;
		pendingDX += e.clientX - lastX;
		pendingDY += e.clientY - lastY;
		lastX = e.clientX;
		lastY = e.clientY;
		scheduleUpdate();
	}

	function handleMouseUp(): void {
		if (!isDragging) return;
		isDragging = false;
		container.style.cursor = 'grab';
		svg.style.pointerEvents = 'auto';

		// Flush any remaining pan
		if (rafId) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		flushUpdate();
	}

	function handleWheel(e: WheelEvent): void {
		e.preventDefault();
		const delta = e.deltaY > 0 ? 0.92 : 1.08;
		// Coalesce: keep latest cursor position, multiply scale deltas
		if (pendingScale !== null) {
			pendingScale *= delta;
		} else {
			pendingScale = state.scale * delta;
		}
		pendingClientX = e.clientX;
		pendingClientY = e.clientY;
		scheduleUpdate();
	}

	// Touch event handlers for mobile pan/pinch
	function getTouchDistance(t1: Touch, t2: Touch): number {
		const dx = t1.clientX - t2.clientX;
		const dy = t1.clientY - t2.clientY;
		return Math.sqrt(dx * dx + dy * dy);
	}

	function getTouchCenter(t1: Touch, t2: Touch): { x: number; y: number } {
		return {
			x: (t1.clientX + t2.clientX) / 2,
			y: (t1.clientY + t2.clientY) / 2
		};
	}

	function handleTouchStart(e: TouchEvent): void {
		if ((e.target as HTMLElement).closest('.node')) return;
		
		isTouching = true;
		
		if (e.touches.length === 2) {
			// Pinch zoom start
			isPinching = true;
			touchStartDistance = getTouchDistance(e.touches[0], e.touches[1]);
			touchStartScale = state.scale;
			const center = getTouchCenter(e.touches[0], e.touches[1]);
			touchCenterX = center.x;
			touchCenterY = center.y;
			svg.style.pointerEvents = 'none';
		} else if (e.touches.length === 1) {
			// Single touch pan start
			isPinching = false;
			lastTouchX = e.touches[0].clientX;
			lastTouchY = e.touches[0].clientY;
			svg.style.pointerEvents = 'none';
		}
	}

	function handleTouchMove(e: TouchEvent): void {
		if (!isTouching) return;
		e.preventDefault(); // Prevent page scroll

		if (isPinching && e.touches.length === 2) {
			// Pinch zoom
			const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
			const scaleDelta = currentDistance / touchStartDistance;
			const center = getTouchCenter(e.touches[0], e.touches[1]);
			
			pendingScale = Math.max(0.25, Math.min(3, touchStartScale * scaleDelta));
			pendingClientX = center.x;
			pendingClientY = center.y;
			scheduleUpdate();
		} else if (e.touches.length === 1) {
			// Single touch pan
			const touch = e.touches[0];
			pendingDX += touch.clientX - lastTouchX;
			pendingDY += touch.clientY - lastTouchY;
			lastTouchX = touch.clientX;
			lastTouchY = touch.clientY;
			scheduleUpdate();
		}
	}

	function handleTouchEnd(e: TouchEvent): void {
		if (e.touches.length === 0) {
			// All touches ended
			isTouching = false;
			isPinching = false;
			svg.style.pointerEvents = 'auto';
			
			// Flush any remaining updates
			if (rafId) {
				cancelAnimationFrame(rafId);
				rafId = null;
			}
			flushUpdate();
		} else if (e.touches.length === 1 && isPinching) {
			// Switched from pinch to single touch - continue panning
			isPinching = false;
			lastTouchX = e.touches[0].clientX;
			lastTouchY = e.touches[0].clientY;
		}
	}

	// Attach event listeners
	container.addEventListener('mousedown', handleMouseDown);
	window.addEventListener('mousemove', handleMouseMove);
	window.addEventListener('mouseup', handleMouseUp);
	container.addEventListener('wheel', handleWheel, { passive: false });
	
	// Touch events - passive: false to prevent default scroll
	container.addEventListener('touchstart', handleTouchStart, { passive: false });
	container.addEventListener('touchmove', handleTouchMove, { passive: false });
	container.addEventListener('touchend', handleTouchEnd);
	container.addEventListener('touchcancel', handleTouchEnd);

	// Cleanup function
	function cleanup(): void {
		container.removeEventListener('mousedown', handleMouseDown);
		window.removeEventListener('mousemove', handleMouseMove);
		window.removeEventListener('mouseup', handleMouseUp);
		container.removeEventListener('wheel', handleWheel);
		container.removeEventListener('touchstart', handleTouchStart);
		container.removeEventListener('touchmove', handleTouchMove);
		container.removeEventListener('touchend', handleTouchEnd);
		container.removeEventListener('touchcancel', handleTouchEnd);
		if (rafId) {
			cancelAnimationFrame(rafId);
		}
	}

	// Initialize
	applyImmediate(true);

	return {
		zoomIn,
		zoomOut,
		resetView,
		fitToScreen,
		cleanup,
		state
	};
}
